import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthToken } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Send, User, Calendar, Mail } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const AdminClients = () => {
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isNotifyOpen, setIsNotifyOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [notifyClient, setNotifyClient] = useState(null);
  const [notifyMessage, setNotifyMessage] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', event_type: '', event_date: ''
  });

  useEffect(() => { fetchClients(); }, []);

  const fetchClients = async () => {
    const token = getAuthToken();
    try {
      const res = await axios.get(`${API}/admin/clients`, { headers: { Authorization: `Bearer ${token}` } });
      setClients(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name || '',
        email: item.email || '',
        password: '',
        event_type: item.event_type || '',
        event_date: item.event_date || ''
      });
    } else {
      setEditingItem(null);
      setFormData({ name: '', email: '', password: '', event_type: '', event_date: '' });
    }
    setIsDialogOpen(true);
  };

  const openNotify = (item) => {
    setNotifyClient(item);
    setNotifyMessage('');
    setIsNotifyOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Name and email are required');
      return;
    }
    if (!editingItem && !formData.password) {
      toast.error('Password is required for new clients');
      return;
    }

    setIsSaving(true);
    const token = getAuthToken();
    try {
      if (editingItem) {
        const updateData = {};
        if (formData.name !== editingItem.name) updateData.name = formData.name;
        if (formData.email !== editingItem.email) updateData.email = formData.email;
        if (formData.password) updateData.password = formData.password;
        if (formData.event_type !== editingItem.event_type) updateData.event_type = formData.event_type;
        if (formData.event_date !== editingItem.event_date) updateData.event_date = formData.event_date;

        if (Object.keys(updateData).length === 0) {
          toast.info('No changes to save');
          setIsDialogOpen(false);
          setIsSaving(false);
          return;
        }

        await axios.put(`${API}/admin/clients/${editingItem.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Client updated!');
      } else {
        const res = await axios.post(`${API}/admin/clients`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const emailStatus = res.data?.email_sent;
        if (emailStatus === 'sent') {
          toast.success('Client created! Welcome email sent.');
        } else if (emailStatus === 'skipped') {
          toast.warning('Client created, but welcome email was skipped (email service not configured).');
        } else {
          toast.success('Client created!');
        }
      }
      setIsDialogOpen(false);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client and all their events?')) return;
    const token = getAuthToken();
    try {
      await axios.delete(`${API}/admin/clients/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Client deleted');
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleNotify = async () => {
    if (!notifyMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    setIsSending(true);
    const token = getAuthToken();
    try {
      const res = await axios.post(`${API}/admin/clients/${notifyClient.id}/notify`, { message: notifyMessage }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const emailStatus = res.data?.email_status;
      if (emailStatus === 'sent') {
        toast.success(`Notification sent to ${notifyClient.name}`);
      } else if (emailStatus === 'skipped') {
        toast.warning('Notification saved but email was skipped (email service not configured).');
      } else {
        toast.success(`Notification sent to ${notifyClient.name}`);
      }
      setIsNotifyOpen(false);
    } catch (error) {
      toast.error('Failed to send notification');
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div data-testid="admin-clients">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-heading text-3xl text-[#2C2C2C]">Client Accounts</h1>
        <Button data-testid="add-client-btn" onClick={() => openDialog()} className="bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full">
          <Plus size={18} className="mr-2" /> New Client
        </Button>
      </div>

      {clients.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-soft p-12 text-center">
          <User className="w-12 h-12 text-[#E8DFD0] mx-auto mb-4" />
          <p className="text-[#6B7280]">No client accounts yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clients.map((item) => (
            <div key={item.id} data-testid={`client-card-${item.id}`} className="bg-white rounded-2xl shadow-soft p-6">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-[#4A5D23] flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-heading text-xl">{item.name?.charAt(0)?.toUpperCase()}</span>
                </div>
                <div className="min-w-0">
                  <h3 className="font-heading text-lg text-[#2C2C2C] truncate">{item.name}</h3>
                  <p className="text-sm text-[#6B7280] truncate">{item.email}</p>
                </div>
              </div>

              {(item.event_type || item.event_date) && (
                <div className="space-y-2 mb-4 text-sm">
                  {item.event_type && (
                    <div className="flex items-center gap-2 text-[#6B7280]">
                      <Calendar size={14} className="text-[#D4AF37]" />
                      <span>{item.event_type}</span>
                    </div>
                  )}
                  {item.event_date && (
                    <div className="flex items-center gap-2 text-[#6B7280]">
                      <Calendar size={14} className="text-[#4A5D23]" />
                      <span>{item.event_date}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => openDialog(item)} className="border-[#E8DFD0]">
                  <Pencil size={14} className="mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => openNotify(item)} className="border-[#D4AF37] text-[#D4AF37]">
                  <Send size={14} className="mr-1" /> Notify
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500 border-red-200">
                  <Trash2 size={14} className="mr-1" /> Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editingItem ? 'Edit Client' : 'Create Client Account'}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#6B7280]">
              {editingItem ? 'Update client account details below.' : 'Fill in the details to create a new client portal account.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Full Name *</Label>
              <Input data-testid="client-name-field" value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} className="input-elegant" placeholder="Client name" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Email Address *</Label>
              <Input data-testid="client-email-field" type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} className="input-elegant" placeholder="client@email.com" />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">
                {editingItem ? 'New Password (leave blank to keep current)' : 'Password *'}
              </Label>
              <Input data-testid="client-password-field" type="password" value={formData.password} onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))} className="input-elegant" placeholder={editingItem ? 'Leave blank to keep current' : 'Set password'} />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Event Type</Label>
              <Select value={formData.event_type} onValueChange={(v) => setFormData(p => ({ ...p, event_type: v }))}>
                <SelectTrigger className="input-elegant"><SelectValue placeholder="Select event type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Wedding">Wedding</SelectItem>
                  <SelectItem value="Anniversary">Anniversary</SelectItem>
                  <SelectItem value="Dinner Party">Dinner Party</SelectItem>
                  <SelectItem value="General Party">General Party</SelectItem>
                  <SelectItem value="Engagement Party">Engagement Party</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Event Date</Label>
              <Input data-testid="client-date-field" type="date" value={formData.event_date} onChange={(e) => setFormData(p => ({ ...p, event_date: e.target.value }))} className="input-elegant" />
            </div>
            <div className="flex gap-4 pt-4">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button data-testid="save-client-btn" onClick={handleSave} disabled={isSaving} className="flex-1 bg-[#D4AF37] hover:bg-[#C5A028] text-white">
                {isSaving ? 'Saving...' : editingItem ? 'Update Client' : 'Create & Send Welcome Email'}
              </Button>
            </div>
            {!editingItem && (
              <p className="text-xs text-[#6B7280] text-center">A welcome email with login credentials will be sent to the client.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Notify Dialog */}
      <Dialog open={isNotifyOpen} onOpenChange={setIsNotifyOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              Send Notification to {notifyClient?.name}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#6B7280]">
              Send an email notification to this client.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            <div className="flex items-center gap-3 p-3 bg-[#F5F5F0] rounded-xl">
              <Mail size={18} className="text-[#D4AF37]" />
              <span className="text-sm text-[#6B7280]">{notifyClient?.email}</span>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Message *</Label>
              <Textarea
                data-testid="notify-message-field"
                value={notifyMessage}
                onChange={(e) => setNotifyMessage(e.target.value)}
                className="input-elegant min-h-[120px]"
                placeholder="Enter the update message to send to your client..."
              />
            </div>
            <div className="flex gap-4">
              <Button variant="outline" onClick={() => setIsNotifyOpen(false)} className="flex-1">Cancel</Button>
              <Button data-testid="send-notify-btn" onClick={handleNotify} disabled={isSending} className="flex-1 bg-[#4A5D23] hover:bg-[#6B7F3A] text-white">
                <Send size={16} className="mr-2" /> {isSending ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminClients;
